import moment, { Moment } from 'moment';
import { get } from 'lodash';
import { Model, isNonSmartChair } from '../_shared/interfaces/model';
import {
  acceptsAnySerial,
  normalizeSnTypeCodes,
} from '../_shared/lib/sn-type-codes';

// AER-1795: equipment serial-number validation shared by Manufacturing Process and Edit
// Equipment. A leaf module (no UI imports) so it can be unit tested without the antd
// dependency chain behind src/utils/index.ts, which re-exports everything here.
//
// The serial <-> model pairing is read from the selected model's `snTypeCodes` and fails
// closed: no model, an unknown model, or a model with no codes rejects the serial, for
// every equipment type. This replaces the hardcoded snTypeMatches table, which was never
// updated as models were added (C5, C8, C12 and C18 were all unbuildable) and let any
// unrecognised code (e.g. every ACS serial) through unchecked.
//
// Every message says what is wrong with the serial as entered and what was expected, and
// a type-code mismatch also names the models the serial's type code does match. The
// reported case was a correct serial rejected with "does not match any expected format",
// which sent people checking the serial instead of the model.

// The two screens speak different equipment-type vocabularies. Manufacturing passes its
// Equipment Type dropdown value (utility / handpiece / chair); Edit Equipment passes the
// model's `type`. Both map onto the serial layout family here rather than relying on
// 'handpiece' and 'chair' happening to be spelled the same in both.
export type SerialRuleFamily = 'utility' | 'handpiece' | 'chair';

const SERIAL_RULE_FAMILY: { [eqType: string]: SerialRuleFamily } = {
  // Manufacturing Process dropdown
  utility: 'utility',
  // model `type`
  compressor: 'utility',
  vacuum: 'utility',
  sterilizer: 'utility',
  aerasone_compressor: 'utility',
  aerasone_vacuum: 'utility',
  // both
  handpiece: 'handpiece',
  chair: 'chair',
};

export const serialRuleFamily = (eqType?: string): SerialRuleFamily =>
  SERIAL_RULE_FAMILY[eqType || ''] || 'utility';

export const normalizeSerialNumber = (serial?: unknown): string =>
  (typeof serial === 'string' ? serial : '').trim().toUpperCase();

// Prototype units carry 'proto' in the serial and have always bypassed serial validation
// on Manufacturing (ignoreValidation()).
export const isPrototypeSerial = (serial?: string): boolean =>
  (serial || '').toLowerCase().indexOf('proto') > -1;

export const findModelById = (
  models: Model[] = [],
  modelId?: string
): Model | undefined => {
  const id = (modelId || '').trim().toUpperCase();
  if (!id) {
    return undefined;
  }
  return (models || []).find((m) => `${get(m, 'id', '')}`.toUpperCase() === id);
};

// --- type codes ---------------------------------------------------------------------------

// Where a model's serial layout keeps the type code, counted from 1 as people do.
interface TypeCodeSlot {
  // what the serial has there (shorter than `size` when the serial is too short)
  token: string;
  size: number;
  where: string;
}

const typeCodeSlot = (
  serial: string,
  family: SerialRuleFamily,
  nonSmartChair: boolean
): TypeCodeSlot => {
  if (family === 'handpiece') {
    // e.g. AR00059AH8
    return { token: serial.slice(-3), size: 3, where: 'its last 3 characters' };
  }
  if (family === 'chair' && nonSmartChair) {
    // 5400 / CRK placeholder, e.g. 00AB12CD
    return {
      token: serial.slice(0, 2),
      size: 2,
      where: 'its first 2 characters',
    };
  }
  if (family === 'chair') {
    // e.g. ABU1035335
    return {
      token: serial.slice(0, 3),
      size: 3,
      where: 'its first 3 characters',
    };
  }
  // e.g. ACR2609104: [A|X] connectivity, then the type code
  return { token: serial.slice(1, 3), size: 2, where: 'characters 2–3' };
};

const fitsTypeCode = (slot: TypeCodeSlot, codes: string[]) =>
  slot.token.length === slot.size && codes.indexOf(slot.token) > -1;

const byModelId = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

// Ids of the models (other than `exceptId`) whose own serial layout finds a type code in
// this serial that they accept. Each candidate is judged by its own layout, so a
// compressor serial typed against a handpiece model still points at the compressors.
// Models that take any serial ('*') are left out: they fit everything, so naming them
// says nothing.
export const modelsMatchingTypeCode = (
  serial: string,
  candidates: Model[] = [],
  exceptId = ''
): string[] => {
  const except = exceptId.toUpperCase();
  const ids = (candidates || [])
    .filter((m) => !!m && `${m.id}`.toUpperCase() !== except)
    .filter((m) => {
      const codes = normalizeSnTypeCodes(get(m, 'snTypeCodes'));
      const slot = typeCodeSlot(
        serial,
        serialRuleFamily(get(m, 'type') as string),
        isNonSmartChair(m.id)
      );
      return !acceptsAnySerial(codes) && fitsTypeCode(slot, codes);
    })
    .map((m) => `${m.id}`);
  return ids.filter((id, i) => ids.indexOf(id) === i).sort(byModelId);
};

// How many model ids a message names before summarising the rest ("and 12 more").
export const MAX_MODELS_NAMED = 10;

const quote = (s: string) => `“${s}”`;

const joinWith = (items: string[], word: string) =>
  items.length > 1
    ? `${items.slice(0, -1).join(', ')} ${word} ${items[items.length - 1]}`
    : items.join('');

const nameModels = (ids: string[]) => {
  if (ids.length <= MAX_MODELS_NAMED) {
    return joinWith(ids, 'and');
  }
  const shown = ids.slice(0, MAX_MODELS_NAMED - 1);
  return `${shown.join(', ')} and ${ids.length - shown.length} more`;
};

const modelsPhrase = (ids: string[]) =>
  `${ids.length === 1 ? 'model' : 'models'} ${nameModels(ids)}`;

const typeCodeMismatch = (
  serial: string,
  model: Model,
  codes: string[],
  slot: TypeCodeSlot,
  selectable: Model[],
  known: Model[]
): string => {
  const expected = `model ${model.id} accepts only ${joinWith(codes.map(quote), 'or')}`;
  if (slot.token.length < slot.size) {
    return `Validation error: Serial number ${serial} is too short to have a type code in ${slot.where}; ${expected}.`;
  }

  let hint = '';
  const fits = modelsMatchingTypeCode(serial, selectable, model.id);
  if (fits.length) {
    hint = `Its type code matches ${modelsPhrase(fits)}.`;
  } else {
    // Say so when the matching model exists but cannot be picked here (hidden from this
    // screen, inactive, or another equipment type), so the user knows what to change.
    const elsewhere = modelsMatchingTypeCode(serial, known, model.id);
    hint = elsewhere.length
      ? `Its type code matches ${modelsPhrase(elsewhere)}, which ${elsewhere.length === 1 ? 'is' : 'are'} not available here.`
      : 'Its type code matches none of the models.';
  }

  return `Validation error: Serial number ${serial} has type code ${quote(slot.token)} in ${slot.where}, but ${expected}. ${hint}`;
};

// --- the check ----------------------------------------------------------------------------

export interface EquipmentSNCheckOptions {
  // Where the selected model's record is looked up: dash.models, or the list the
  // Manufacturing model dropdown was built from.
  models?: Model[];
  // The models the user can pick on this screen (the model dropdown's list). A type-code
  // mismatch names the ones the serial matches. Defaults to `models`.
  selectableModels?: Model[];
  // Build year must be the current or previous year. Right while a unit is being built;
  // wrong on Edit Equipment, where older units get corrected. Default true.
  checkBuildYear?: boolean;
  // Upper length bound for utility serials (Edit Equipment: 12, from AER-1597).
  maxLength?: number;
  // Clock for the build-year window; tests pin it.
  now?: Moment;
}

export const _checkEquipmentSN = (
  eSN: string,
  mId?: string,
  eqType?: string,
  opts: EquipmentSNCheckOptions = {}
): Error[] => {
  const {
    models = [],
    selectableModels = models,
    checkBuildYear = true,
    maxLength,
    now = moment(),
  } = opts;
  const ESN = normalizeSerialNumber(eSN);
  const errors: Error[] = [];
  const fail = (message: string) => {
    errors.push(new Error(message));
  };
  // what the serial has at a position, for messages
  const has = (part: string) =>
    part ? `${ESN} has ${quote(part)}` : `${ESN} is too short`;

  // Empty is the `required` rule's job; prototypes are exempt.
  if (!ESN || isPrototypeSerial(ESN)) {
    return errors;
  }

  // --- serial <-> model pairing: fail closed -------------------------------------------
  const modelId = (mId || '').trim();
  if (!modelId) {
    return [
      new Error(
        `Validation error: Select an equipment model to validate serial number ${ESN}.`
      ),
    ];
  }
  const model = findModelById(models, modelId);
  if (!model) {
    return [
      new Error(
        `Validation error: Serial number ${ESN} cannot be validated: model ${modelId} was not found.`
      ),
    ];
  }
  const codes = normalizeSnTypeCodes(get(model, 'snTypeCodes'));
  if (!codes.length) {
    return [
      new Error(
        `Validation error: Serial number ${ESN} cannot be validated: model ${model.id} has no serial number type codes configured. An admin can add them under Models → ${model.id} → Edit.`
      ),
    ];
  }
  if (acceptsAnySerial(codes)) {
    return errors;
  }

  // --- per-type layout: the type code sits in a different position for each family ----
  const family = serialRuleFamily(eqType);
  const slot = typeCodeSlot(ESN, family, isNonSmartChair(model.id));
  const checkTypeCode = () => {
    if (!fitsTypeCode(slot, codes)) {
      fail(typeCodeMismatch(ESN, model, codes, slot, selectableModels, models));
    }
  };
  const exactLength = (kind: string, length: number) => {
    if (ESN.length !== length) {
      fail(
        `Validation error: ${kind} Serial Number must be exactly ${length} characters; ${ESN} has ${ESN.length}.`
      );
    }
  };

  if (family === 'handpiece') {
    // e.g. AR00059AH8
    exactLength('Handpiece', 10);
    if (!/^[a-zA-Z()]+$/.test(ESN.slice(0, 2))) {
      fail(
        `Validation error: Handpiece Serial Number must start with 2 letters; ${has(ESN.slice(0, 2))}.`
      );
    }
    if (isNaN(parseInt(ESN.slice(2, 7), 10))) {
      fail(
        `Validation error: Characters 3–7 of a Handpiece Serial Number must be a number; ${has(ESN.slice(2, 7))}.`
      );
    }
    checkTypeCode();
    return errors;
  }

  if (family === 'chair') {
    if (isNonSmartChair(model.id)) {
      // AER-1675: non-smart Forest chairs (5400/CRK) use an 8-character placeholder:
      // type code, then 6 letters or digits (e.g. 00AB12CD).
      exactLength('Chair', 8);
      checkTypeCode();
      const rest = ESN.slice(2, 8);
      if (!/^[0-9A-Z]*$/.test(rest)) {
        fail(
          `Validation error: Characters 3–8 of a Chair Serial Number must be letters or digits; ${has(rest)}.`
        );
      }
      return errors;
    }

    // Smart chairs, e.g. ABU1035335
    exactLength('Chair', 10);
    checkTypeCode();
    if (isNaN(parseInt(ESN.slice(3, 10), 10))) {
      fail(
        `Validation error: Characters 4–10 of a Chair Serial Number must be a number; ${has(ESN.slice(3, 10))}.`
      );
    }
    return errors;
  }

  // Utility, e.g. ACR2609104: [A|X] connectivity, type code in chars 2-3, then
  // YY (build year), MM (build month) and a 3-digit sequence.
  checkTypeCode();

  if (maxLength && ESN.length > maxLength) {
    fail(
      `Validation error: Equipment Serial Number must be ${maxLength} characters or fewer; ${ESN} has ${ESN.length}.`
    );
  }

  // A placeholder with seven identical trailing characters (ACR0000000) skips the date checks.
  const rest = [...ESN.slice(3)];
  const allSame = rest.length === 7 && rest.every((x, _, a) => x === a[0]);

  if (checkBuildYear && !allSame) {
    const snYear = ESN.slice(3, 5);
    const thisYear = `${now.year()}`.slice(2);
    const lastYear = `${moment(now).subtract(1, 'year').year()}`.slice(2);
    if (snYear !== thisYear && snYear !== lastYear) {
      fail(
        `Validation error: Characters 4–5 must be the build year, ${quote(thisYear)} or ${quote(lastYear)}; ${has(snYear)}.`
      );
    }
  }

  const snMonthText = ESN.slice(5, 7);
  const snMonth = parseInt(snMonthText, 10);
  if (!allSame && !(!!snMonth && snMonth > 0 && snMonth <= 12)) {
    fail(
      `Validation error: Characters 6–7 must be the build month, 01–12; ${has(snMonthText)}.`
    );
  }

  return errors;
};
